import { Typography, Stack, Grid, Chip, Popper, Paper } from '@mui/material';
import { useNavigate, useSearchParams, createSearchParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/system';

export const TAG_QUERY_PARAM = 'tagged'

function removeTag(tagToRemove, searchParams) {
  const updatedParams = new URLSearchParams(searchParams.toString());
  const tagged = updatedParams.get(TAG_QUERY_PARAM);
  if (!tagged) return updatedParams;

  const tags = tagged.split(',').filter(tag => tag !== tagToRemove);
  if (tags.length > 0) {
    updatedParams.set(TAG_QUERY_PARAM, tags.join(','));
  } else {
    updatedParams.delete(TAG_QUERY_PARAM);
  }

  return updatedParams;
}

function addTag(tagToAdd, searchParams) {
  const updatedParams = new URLSearchParams(searchParams.toString());

  const tagged = updatedParams.get(TAG_QUERY_PARAM);

  const tags = tagged?.split(',') || []
  if(tags.indexOf(tagToAdd) === -1) {
    updatedParams.set(TAG_QUERY_PARAM, [...tags, tagToAdd].join(','));
  }

  return updatedParams
}

export function Tag(props = {tag: '', onClick: undefined, onMouseDown: undefined, clickable: false, onDelete: undefined, sx: undefined}) {
  const {
    tag,
    onClick,
    onMouseDown,
    clickable,
    onDelete,
    sx,
  } = props
  return <Chip
    className='tag'
    key={tag}
    size='small'
    onMouseDown={onMouseDown}
    onClick={onClick}
    label={tag}
    clickable={clickable}
    onDelete={onDelete}
    sx={sx}
  />
}

export function AppliedFilters(props = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = (searchParams.get(TAG_QUERY_PARAM))?.split(',') || []
  const {
    sx
  } = props
  return (
    <Stack sx={sx} direction="row" gap={1} alignItems='center'>
      <Typography variant='subtitle1' fontWeight='bold'>Tagged:</Typography>
      {filters.map((f, i) => {
        const tag = f.replace(/tag:/, '')
        return <Tag
          key={'tag-' + i}
          clickable
          size='small'
          tag={tag}
          onDelete={() => {
            setSearchParams(removeTag(tag, searchParams))
          }}
        />
      })
      }
    </Stack>
  )
}

export default function Tags(props = { tags: undefined, onSelectTag: undefined, clickable: false, sx: undefined }) {
  const navigate = useNavigate();
  const {
    tags,
    onSelectTag = () => {},
    clickable,
    sx,
    className,
    sitePath,
  } = props;

  const [searchParams, setSearchParams] = useSearchParams();

  const anchorRef = useRef(null);
  const measureRef = useRef(null);
  const moreRef = useRef(null)
  const [measureKey, setMeasureKey] = useState(0)

  const [visibleCount, setVisibleCount] = useState(null);
  const [open, setOpen] = useState(false);

  const tagsArray = useMemo(
    () =>
      tags
        ?.split(',')
        .reduce((s, tag) => {
          if (tag.indexOf('tag:') > -1) s.push(tag.split('tag:')[1]);
          return s;
        }, []) ?? [],
    [tags]
  );

  /**
   * Measure how many tags fit into the clamped container height.
   * This runs once after mount / tags change.
   */
  useEffect(() => {
  if (!measureRef.current || !anchorRef.current) return;

  const maxHeight = anchorRef.current.clientHeight;
  const children = Array.from(measureRef.current.children);

  let fitCount = 0;

  for (let i = 0; i < children.length; i++) {
    const el = children[i];
    const bottom = el.offsetTop + el.offsetHeight;

    if (bottom <= maxHeight) {
      fitCount++;
    } else {
      break;
    }
  }

  if (fitCount >= tagsArray.length) {
    setVisibleCount(null);
    return;
  }

  setVisibleCount(Math.max(fitCount - 1, 0));
}, [tagsArray.length, measureKey]);

useEffect(() => {
  if (!anchorRef.current) return;

  const ro = new ResizeObserver(() => {
    setMeasureKey(k => k + 1);
  });

  ro.observe(anchorRef.current);
  return () => ro.disconnect();
}, []);

  const handleTagClick = (tag, event) => {
    event.stopPropagation();
    event.preventDefault();
    if (onSelectTag) onSelectTag();
    if (!clickable) return;

    const nextParams = addTag(tag, searchParams);
    const search = createSearchParams(nextParams).toString();

    if (sitePath) {
      navigate(`/${sitePath}?${search}`);
    } else {
      setSearchParams(addTag(tag, searchParams));
    }
  };

  const visibleTags =
    visibleCount != null ? tagsArray.slice(0, visibleCount) : tagsArray;

  const hiddenCount = visibleCount != null && visibleCount < tagsArray.length
  ? tagsArray.length - visibleCount
  : 0;
  // const hiddenCount =
  //   visibleCount != null ? tagsArray.length - visibleCount : 0;

  return (
    <>
      {/* Visible / clamped view */}
      <Stack
        ref={anchorRef}
        sx={{...sx, width: '100%'}}
        className={`tags-container${className ? ' ' + className : ''}`}
        gap={0.5}
        direction="row"
        justifyItems="flex-end"
        flexWrap="nowrap"
      >
        {visibleTags.map(tag => (
          <Tag
            key={tag}
            clickable
            tag={tag}
            onClick={e => handleTagClick(tag, e)}
            onMouseDown={e => e.stopPropagation()}
          />
        ))}

        {hiddenCount > 0 && (
          <Box ref={moreRef}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            className={`tags-container${className ? ' ' + className : ''}`}
          >
          <Tag
            clickable={false}
            tag={`+${hiddenCount} more`}
          />
          </Box>
        )}
      </Stack>

      {/* Hidden measurement container (off-screen) */}
      <Stack
        ref={measureRef}
        gap={0.5}
        direction="row"
        flexWrap="wrap"
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          width: anchorRef.current?.clientWidth,
          height: anchorRef.current?.clientHeight,
          overflow: 'hidden'
        }}
      >
        {tagsArray.map(tag => (
          <Tag key={tag} tag={tag} />
        ))}
      </Stack>

      {/* Popper with full tag list */}
      <Popper
        open={open}
        anchorEl={moreRef.current}
        placement="top"
        disablePortal={false}
        modifiers={[
          {
            name: 'offset',
            options: { offset: [0,0] },
          },
        ]}
        sx={{
          zIndex: theme => theme.zIndex.tooltip
        }}
      >
        <Paper
          elevation={5}
          sx={{
            p: .5,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            maxWidth: 320,
            zIndex: theme => theme.zIndex.tooltip
          }}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          className={`tags-container${className ? ' ' + className : ''}`}
        >
          {tagsArray.map(tag => (
            <Tag
              key={tag}
              clickable
              tag={tag}
              onClick={e => handleTagClick(tag, e)}
              onMouseDown={e => e.stopPropagation()}
            />
          ))}
        </Paper>
      </Popper>
    </>
  );
}

// export default function Tags(props = {tags: undefined, onSelectTag: undefined, clickable: false, sx: undefined}) {

//   const navigate = useNavigate()
//   const {
//     tags,
//     onSelectTag = () => { },
//     clickable,
//     sx,
//     className,
//     sitePath
//   } = props

//   const [searchParams, setSearchParams] = useSearchParams();

//   const tagsArray = tags
//     .split(',')
//     .reduce((s, tag) => {
//       if (tag.indexOf('tag:') > -1) s.push(tag.split('tag:')[1])
//       return s
//     }, [])

//   return (
//     <Stack sx={sx} className={`tags-container${className ? ' ' + className : ''}`} gap={0.5} direction='row' justifyItems='flex-start' flexWrap='wrap'>
//       {tagsArray.map((tag, i) => {
//         return <Tag
//           key={tag}
//           clickable
//           onClick={(event) => {
//             event.stopPropagation();
//             event.preventDefault();
//             if (onSelectTag) onSelectTag()
//             if (!clickable) return;

//             const nextParams = addTag(tag, searchParams);
//             const search = createSearchParams(nextParams).toString();


//             if(sitePath) {
//               const path = `/${sitePath}?${search}`
//               navigate(path)
//             } else {
//               setSearchParams(addTag(tag, searchParams))
//             }

//           }}
//           onMouseDown={event => event.stopPropagation()}
//           tag={tag}
//         />
//       })}
//     </Stack>
//   )
// }